"use client";

import { useState, useRef, useEffect } from "react";
import { colorPresets } from "@/lib/themes";

interface ColorPresetPickerProps {
  value: string;
  onChange: (presetId: string) => void;
}

export default function ColorPresetPicker({ value, onChange }: ColorPresetPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedPreset = colorPresets.find((p) => p.id === value) ?? colorPresets[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="rounded-lg border border-border p-1.5 px-3 font-medium text-sm text-foreground cursor-pointer flex items-center gap-2 hover:bg-muted transition-colors"
      >
        <span
          className="w-4 h-4 rounded-full shrink-0"
          style={{
            backgroundColor: selectedPreset.accent,
            boxShadow: `0 0 0 2px var(--background), 0 0 0 3px ${selectedPreset.accent}40`,
          }}
        />
        <span>{selectedPreset.name}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+4px)] right-0 bg-card border border-border rounded-lg shadow-lg p-1 min-w-[200px] z-50 animate-[dropdownEnter_0.15s_ease-out]">
          {colorPresets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => {
                onChange(preset.id);
                setOpen(false);
              }}
              className={`flex items-center gap-2.5 w-full text-left py-2 px-3 rounded border-none cursor-pointer transition-colors ${
                preset.id === value ? "bg-muted" : "bg-transparent hover:bg-muted"
              }`}
            >
              <span
                className={`w-8 h-8 rounded-lg border border-border cursor-pointer shrink-0 ${
                  preset.id === value ? "ring-2 ring-foreground ring-offset-2 ring-offset-background" : ""
                }`}
                style={{ backgroundColor: preset.accent }}
              />
              <span className="flex-1 font-medium text-sm text-card-foreground">
                {preset.name}
              </span>
              {preset.id === value && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="text-foreground shrink-0"
                >
                  <path
                    d="M3 7L6 10L11 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
